import type {
  CollabDraftAttributeTestColumn,
  CollabRemovedItem,
} from "../../../models/audit/collabPlanTypes";

/**
 * Rendering helpers for a revised plan's removed items.
 *
 * These name no entity: one splices ghosts back into a list, the other derives a
 * column's display key, so an entity's `planFreeTextDiff` composes them rather
 * than carrying its own copies.
 */

/** A list entry that is either a live item or a ghost of a removed one. */
export type GhostedEntry<T> =
  | { kind: "live"; item: T; index: number }
  | { kind: "removed"; item: T };

/**
 * Splices removed items back into the live list at their original positions,
 * so ghosts render where the items used to live instead of at the end.
 * `index` on live entries is the item's position in the CURRENT list, for
 * callbacks that mutate by index.
 */
export function withRemovedInPlace<T>(
  current: readonly T[],
  removed: readonly CollabRemovedItem<T>[],
): GhostedEntry<T>[] {
  const entries: GhostedEntry<T>[] = current.map((item, index) => ({
    kind: "live",
    item,
    index,
  }));
  for (const ghost of [...removed].sort((a, b) => a.index - b.index)) {
    entries.splice(Math.min(ghost.index, entries.length), 0, {
      kind: "removed",
      item: ghost.item,
    });
  }
  return entries;
}

/**
 * How a test column is identified in a field key and in removal matching: its
 * name, else the attribute it tests, else its id. Never its index — a column
 * that moves must keep the same key.
 */
export const attributeTestColumnKey = (
  column: CollabDraftAttributeTestColumn,
): string =>
  column.name ??
  column.attributeReference ??
  (column.id ? `#${column.id}` : "");
